import type { JsonApiDoc, JsonApiResource } from 'tio-data/demo/types';
import type { DemoFixtureIdentity } from 'tio-employee/demo/types';
import { buildMatchParticipantGraph } from 'tio-employee/demo/products/employer-match-fixtures';
import {
  getDemoData,
  getDemoDebtLevel,
  getDemoUserScenario,
  getDemoSessionRole,
  isPslfEligibleClient,
  type DemoSessionRoleId,
  type DemoSessionSelectionSnapshot,
} from 'tio-employee/utils/career-pathways-demo-data';

type ResolvedSelection = DemoSessionSelectionSnapshot;

/**
 * Fabricated `/me` identity document for an active demo session.
 *
 * `SessionContextService.load()` does a raw `fetch(ENV.apiHost + '/me?...')`,
 * then `store.pushPayload(parsed)` + `peekRecord('user', parsed.data.id)`, and
 * derives `this.roles` from `user.uniqueRolesByRelationshipType`. The demo-mode
 * fetch interceptor serves this doc for that request, so the whole session is
 * demo-coherent (greeting, sidebar, role gating, terms gate) without touching
 * the real backend.
 *
 * EVERY UserModel relationship is `async: false`, so every resource referenced
 * by `data` (transitively) MUST appear in `included` or it materializes empty.
 *
 * Role graph (drives `user.computedRoleViews`): the getter iterates
 * `userRole.relationships[].jurisdiction.company` (NOT `userRole.company`)
 * unless `userRole.employee` is set. So the chain is:
 *   user -> user-roles[0] -> relationships[0] -> jurisdiction (a user-role)
 *        -> company -> company-setting
 * The relationship's `relationshipType` is what `applyDemoSession` persists to
 * `localStorage('role')` (`getDemoSessionRole(roleId).relationshipType`), which the
 * demo module reads back to rebuild this doc. `sessionContext.currentRole` no longer
 * consults that key — it takes the first role held at the current company — but this
 * doc yields exactly ONE role view, so the picked role is still the resolved one.
 *
 * STAFF-GATE NOTE (intentional, not resolvable here): `demoMode.isStaffUser`
 * (demo-mode.ts) checks `currentRole.relationshipType` against
 * `STAFF_RELATIONSHIP_TYPES = { 'TAS.Admin.SUPERUSER', 'TAS.Approver.TIO_APPROVER' }`.
 * The three demo session relationshipTypes (PARTICIPANT / TAS.Approver.SUPERVISOR /
 * EMPLOYEE_ADMIN) are NONE of those, so `isStaffUser` is false for every demo
 * role. We deliberately stamp the PERSISTED relationshipType (not a staff type), so
 * the role the user picked is the one that resolves. The staff set only gates
 * `isAvailable` when
 * `tioEnvironment === 'production'` AND `demoModeRequireStaffRoleInProd` is on
 * (off by default), so the demo stays active everywhere today. Reconciling the two
 * (so prod-with-the-flag keeps demo active) is a DEMO_SESSION_ROLES /
 * STAFF_RELATIONSHIP_TYPES change, NOT a `/me` doc change — do not "fix" it here.
 *
 * Terms gate (`sessionContext.hasAcceptedAppTerms`) ANDs `usageAgreement`,
 * `privacyAgreement`, and every custom-register agreement; the usage + privacy
 * agreement resources are sideloaded so the demo user is never trapped on the
 * terms screen. (`hasAcceptedAllCustomRegisterTerms` is vacuously true in a demo
 * session because `preRegister.customTerms` is empty — the registration flow that
 * populates it is bypassed — so the custom agreement below is harmless belt-and-
 * suspenders, not load-bearing.)
 */

// User identity is stable across selections so `peekRecord('user', id)` and the
// `findRecord('user', id)` the authenticated route re-issues both resolve.
// Exported so the registry can build the `/users/<id>` fetch-matcher that serves
// this same identity to the route's findRecord BUILDER read (see registry.ts).
export const USER_ID = 'demo-user';
// Exported alongside USER_ID so the registry can build the `/people/<id>` fetch
// matcher that serves the session person to builder findRecord reads (the
// finance/history + loans/detail routes re-load the person via the builder).
export const PERSON_ID = 'demo-person';
const USER_ROLE_ID = 'demo-user-role-1';
const USER_RELATIONSHIP_ID = 'demo-user-relationship-1';
const JURISDICTION_ROLE_ID = 'demo-jurisdiction-role-1';
const PRIMARY_EMAIL_ID = 'demo-email-primary';
const USAGE_AGREEMENT_ID = 'demo-agreement-usage';
const PRIVACY_AGREEMENT_ID = 'demo-agreement-privacy';
const CUSTOM_AGREEMENT_ID = 'demo-agreement-custom-register';
const CUSTOM_CONDITION_ID = 'demo-condition-custom-register';

// Reused from tas-fixtures.ts so the identity employee/company/company-setting
// do not diverge from the TAS dashboard fixtures (same ids => same records).
export const EMPLOYEE_ID = 'demo-tas-employee-1';
const COMPANY_ID = 'demo-tas-company-1';
const COMPANY_SETTING_ID = 'demo-tas-companysetting-1';

const PRIMARY_EMAIL_ADDRESS = 'demo.user@example.com';
const DEFAULT_FIRST_NAME = 'Demo';
const DEFAULT_LAST_NAME = 'User';

interface IdentityName {
  firstName: string;
  lastName: string;
}

/**
 * Resolve the demo user's display name. Prefers the per-client
 * `DemoClientData.employee` slot (first/last); else splits the active user
 * scenario's `name` (e.g. "Power User") into first/last; else a static default.
 *
 * NOTE: this name drives the greeting/sidebar via `sessionContext.person`, which
 * resolves to the /me doc's person ONLY when the active scenario registers no
 * `findRecord('person')` matcher (e.g. the TAS product registers only an
 * employee findRecord). A scenario that DOES register a demo person would shadow
 * this name — by design.
 */
// company-setting reused from tas-fixtures, carrying BOTH the tuitionAssistance
// block and the SLR `modules.syf` flag, so currentEmployee.company.companySetting
// is coherent for every product (TAS + contributions) off the one demo identity.
// Shared by the /me identity doc AND the standalone company findRecord doc so the
// two can never drift.
function buildDemoCompanySettingResource(): JsonApiResource {
  return {
    type: 'company-setting',
    id: COMPANY_SETTING_ID,
    attributes: {
      tuitionAssistance: {
        hideAnnualBenefitsCard: false,
        isDependentProgramsEnabled: false,
      },
      modules: { syf: { isEnabled: true } },
      // Eligibility-file cards read companySetting.eligibilityDefault.autoProcess
      // (with a legacy 'auto-process' fallback) once a companySetting exists.
      eligibilityDefault: { autoProcess: true },
    },
  };
}

// company id reused from tas-fixtures, name overridden to the per-client demo
// employer so the sidebar/header employer matches the selected client.
function buildDemoCompanyResource(employerName: string): JsonApiResource {
  return {
    type: 'company',
    id: COMPANY_ID,
    attributes: { name: employerName, isActive: true },
    relationships: {
      companySetting: { data: { type: 'company-setting', id: COMPANY_SETTING_ID } },
      // async:false — a missing linkage resolves undefined/null and the
      // shipped PSLF route reads `company.pslfGroups` directly.
      pslfGroups: { data: [] },
    },
  };
}

/**
 * Standalone `company` findRecord document for the active demo session, served
 * by a catch-all `findRecord('company')` matcher registered LAST (so a
 * product-specific company fixture, e.g. PSLF's, still wins). The handler
 * echoes the REQUESTED company id onto this doc, so ANY company lookup during a
 * demo — including ids only referenced via a sideloaded relationship (e.g.
 * `demo-slr-company-1`) — resolves to the demo company + company-setting
 * instead of falling through to the real API and 404-ing. This is the
 * defense-in-depth half of the demo-leak fix: an ACTIVE demo session can never
 * 404 on a company.
 */
export function buildDemoCompanyDoc(selection: ResolvedSelection): JsonApiDoc {
  const employerName = getDemoData(selection.clientId).client.name;
  return {
    data: buildDemoCompanyResource(employerName),
    included: [buildDemoCompanySettingResource()],
  };
}

/**
 * The single source of truth for the demo session's display identity. The
 * registry resolves this once per request and threads it through every product
 * builder, so product fixtures (TAS approval queue, contributions person, PSLF
 * borrower/employer) always carry the SAME employee + employer names as the
 * `/me` identity — hardcoded fixture names are how the header said
 * "Lifepoint Health" while the approval queue said "Acme Corporation".
 */
export function resolveDemoFixtureIdentity(selection: ResolvedSelection): DemoFixtureIdentity {
  return {
    ...resolveIdentityName(selection),
    employerName: getDemoData(selection.clientId).client.name,
    clientId: selection.clientId,
  };
}

/**
 * Short plan/company code derived from the employer name ("Lifepoint Health"
 * → "LH"). Several admin surfaces concatenate `plan.companyKey` with the plan
 * name, so every demo plan resource must carry it or screens render
 * "undefined - <plan>".
 */
export function demoCompanyKey(employerName: string): string {
  return (
    employerName
      .split(/\s+/)
      .map((word) => word[0] ?? '')
      .join('')
      .toUpperCase()
      .slice(0, 4) || 'DEMO'
  );
}

/**
 * Standalone `employee` findRecord document, served by a catch-all
 * `findRecord('employee')` matcher registered LAST (product employee fixtures
 * — TAS/contributions/PSLF — win first). The handler echoes the requested id,
 * so admin drill-ins into ANY employee row (e.g. the directory in a non-TAS
 * demo) resolve to a coherent demo employee instead of the live API. The
 * shape mirrors the TAS product employee (which the admin show surfaces are
 * verified against), including the unguarded reads: customAttributes,
 * employmentType, and the participant/company graph.
 */
export function buildDemoEmployeeCatchAllDoc(selection: ResolvedSelection): JsonApiDoc {
  const employerName = getDemoData(selection.clientId).client.name;
  // STRUCTURAL attributes only — no names/email/payroll. The handler echoes
  // the requested id and store.push MERGES attributes, so a doc carrying
  // identity names would rename whichever employee row (directory colleague,
  // queue applicant) the admin drilled into. Rows reached from a list have
  // their display attributes pushed by that list already; this doc just
  // guarantees the unguarded structural reads (customAttributes,
  // employmentType, participant/company/plan graph) resolve.
  return {
    data: {
      type: 'employee',
      id: EMPLOYEE_ID,
      attributes: {
        participationStatus: 'participating',
        isActive: true,
        customAttributes: {},
        employmentType: 'FULL_TIME',
        eligibilityStartDate: '2022-02-01',
        createdAt: '2022-01-15T09:00:00Z',
        statistics: { user: { total_accounts: 1, total_loans: 2 } },
      },
      relationships: {
        company: { data: { type: 'company', id: COMPANY_ID } },
        tasParticipant: { data: { type: 'tas-participant', id: 'demo-tas-participant-1' } },
        plan: { data: { type: 'plan', id: 'demo-slr-plan-1' } },
      },
    },
    included: [
      buildDemoCompanyResource(employerName),
      buildDemoCompanySettingResource(),
      {
        type: 'tas-participant',
        id: 'demo-tas-participant-1',
        attributes: { state: 'TAS.ParticipantState.ACTIVE', employmentStartDate: '2022-01-15' },
        relationships: { tasEligibilities: { data: [] }, tasGroupings: { data: [] } },
      },
      {
        type: 'plan',
        id: 'demo-slr-plan-1',
        attributes: {
          name: `${employerName} Student Loan Repayment`,
          companyKey: demoCompanyKey(employerName),
        },
      },
    ],
  };
}

/**
 * The session person's linked-loan graph, DRIVEN BY THE DEBT AXIS — shared by
 * the standalone person doc below AND the /me identity doc, because the /me
 * bootstrap materializes the person FIRST: if /me shipped an accounts-less
 * person, the dashboard's later `findRecord('person', { include:
 * 'accounts.loans,…' })` would be a cache HIT on that empty graph and the
 * loan-summary tile would render $0.00 despite a correct /people fixture.
 * One canonical graph on every seam keeps the cache coherent.
 *
 * UNITS (wire truth, tio-api loan.rb `convert_to_currency_float`): the API
 * serializes `loan.observation.balance` (and originalAmount) as DOLLARS —
 * `LoanModel.currentBalance` returns it raw and the shipped dashboard's
 * loan-summary tile formats it as USD directly. A cents value here renders a
 * 100x ($4.5M-class) balance. The account-level `observation.balance`
 * (`AccountModel.loanTotal`) is dollars; `minimum-monthly-payment` IS cents
 * (`loanMinMonthlyPayment` divides by 100).
 */
function buildPersonLoanGraph(selection: ResolvedSelection): {
  accountRefs: Array<{ type: string; id: string }>;
  included: JsonApiResource[];
} {
  const debt = getDemoDebtLevel(selection.debtLevelId);
  if (!debt.hasLinkedLoans || debt.balanceDollars === null) {
    // `not-linked` keeps the person account-less — the real add-loan state.
    return { accountRefs: [], included: [] };
  }

  const balanceDollars = debt.balanceDollars;
  // Principal/interest split of the current balance (DOLLARS on the wire — the
  // account-activity detail table reads account.providerRegularMonthlyPayment /
  // principalBalance / interestBalance and loan.principalBalance /
  // interestBalance raw, all in the loan.rb `convert_to_currency_float` set).
  const principalDollars = Math.round(balanceDollars * 0.94);
  const interestDollars = balanceDollars - principalDollars;
  // A PSLF demo borrower's linked loans are federal (NSLDS-tracked). Marking the
  // account/loan with NSLDS PSLF data lights up the shipped PSLF dashboard's
  // Qualifying Payments Tracker + hero (account.ext/loan.ext `hasNsldsPslfData`);
  // gated on PSLF being active so non-PSLF demos (account-activity, contributions)
  // keep the plain debt-axis graph unchanged. `not-linked` still returns no
  // accounts above — the real "upload your MyStudentData" onboarding state.
  // Derived PURELY from the passed selection (not global demo state): the
  // demo-mode service builds `productIds`/`clientId` from the same source
  // `isFeatureEnabled('pslf')` reads, and PSLF is masked for for-profit clients
  // (`isPslfEligibleClient`) — so this equals `features.pslf` without making the
  // fixture builder depend on localStorage.
  const pslfActive =
    selection.productIds.includes('pslf') && isPslfEligibleClient(selection.clientId);
  const account: JsonApiResource = {
    type: 'account',
    id: 'demo-person-account-1',
    attributes: {
      status: 'ACTIVE',
      isActive: true,
      number: '****2210',
      publicOrdinalValue: 1,
      observation: {
        balance: balanceDollars,
        'interest-weighted-average': debt.interestRate,
        // minimum-monthly-payment is CENTS (loanMinMonthlyPayment divides by
        // 100); provider-regular-monthly-payment / principal-balance /
        // interest-balance are DOLLARS (read raw by account.ext for the
        // account-activity Monthly Payment / Principal / Interest summary).
        'minimum-monthly-payment': debt.monthlyPaymentCents,
        'provider-regular-monthly-payment': debt.monthlyPaymentDollars,
        'principal-balance': principalDollars,
        'interest-balance': interestDollars,
        'payable-account-number': '****2210',
        'provider-success-at': '2026-06-01T00:00:00Z',
        'is-paid-off': false,
        ...(pslfActive ? { provider: 'ObservableProvider.NSLDS' } : {}),
      },
      summary: { loanCount: 1 },
    },
    relationships: {
      financialInstitution: {
        data: { type: 'financial-institution', id: 'demo-person-fi-1' },
      },
      loans: { data: [{ type: 'loan', id: 'demo-person-loan-1' }] },
      postalAddress: { data: { type: 'postal-address', id: 'demo-person-addr-1' } },
      person: { data: { type: 'person', id: PERSON_ID } },
    },
  };

  const loan: JsonApiResource = {
    type: 'loan',
    id: 'demo-person-loan-1',
    attributes: {
      isActive: true,
      observation: {
        // Loan-level balance is DOLLARS on the wire (see units note above).
        balance: balanceDollars,
        // Display fields live INSIDE the observation blob under the keys the
        // loan extension reads (loan.ext.ts): `name` ← 'loan-name',
        // `lastUpdatedOn` ← 'at', `originalAmount` ← 'original-amount',
        // `originalDate` ← 'original-date', `interestRate` ← 'interest-rate',
        // `principalBalance` ← 'principal-balance', `interestBalance` ←
        // 'interest-balance', `repaymentPlan` ← 'repayment-plan-derived',
        // `loanTypeDisplayName` ← 'raw-loan-type', `status` ← 'status-derived'.
        // These populate the account-activity loan-detail table (name/type/
        // plan/status/original/principal/interest) — without them each column
        // renders blank/$0.00. Top-level name/lastUpdatedOn are not schema
        // fields and would be silently dropped.
        'loan-name': 'Direct Consolidated Unsubsidized',
        'raw-loan-type': 'Direct Consolidated Unsubsidized',
        status: 'REPAYMENT',
        'status-derived': 'LoanStatus.REPAYMENT',
        'repayment-plan-derived': `${debt.repaymentPlan} Repayment`,
        at: '2026-06-01',
        'original-date': '2019-09-01',
        'original-amount': Math.round(balanceDollars * 1.15),
        'interest-rate': debt.interestRate,
        'principal-balance': principalDollars,
        'interest-balance': interestDollars,
        'next-payment-date': '2026-08-01',
        // Qualifying-payment count the tracker renders as "78/120"
        // (loan.ext `hasNsldsPslfData` keys off this; `nsldsUpdatedAt` ← `at`).
        ...(pslfActive ? { 'forgiveness-program-qualified': 78 } : {}),
      },
    },
    relationships: {
      account: { data: { type: 'account', id: 'demo-person-account-1' } },
    },
  };

  const financialInstitution: JsonApiResource = {
    type: 'financial-institution',
    id: 'demo-person-fi-1',
    attributes: { name: 'MOHELA', isActive: true },
  };

  const postalAddress: JsonApiResource = {
    type: 'postal-address',
    id: 'demo-person-addr-1',
    attributes: {
      // Schema field names (postal-address.schema.ts): addressLine1 /
      // postalCode — not address1 / zipCode, which would be dropped.
      addressLine1: '2210 Demo Way',
      city: 'Columbus',
      state: 'OH',
      postalCode: '43004',
    },
  };

  return {
    accountRefs: [{ type: 'account', id: 'demo-person-account-1' }],
    included: [account, loan, financialInstitution, postalAddress],
  };
}

function buildPslfProfileResource(): JsonApiResource {
  return {
    type: 'pslf-profile',
    id: 'demo-pslf-profile-1',
    attributes: {
      state: 'PslfProfileState.ACTIVE',
      qualifyingPaymentsMade: 78,
      employerEligibility: 'eligible',
    },
    relationships: {
      employee: { data: { type: 'employee', id: EMPLOYEE_ID } },
    },
  };
}

/**
 * The session employee served for the FINDRECORD BUILDER read the shipped
 * PSLF route makes (`findRecord('employee', id, { include:
 * 'pslf-profile,pslf-forms,employee-addition', reload: true })` — reload
 * forces the fetch seam). Extends the structural catch-all with the PSLF
 * graph: a present `pslfProfile` PRE-EMPTS the route's
 * `createRecord('pslf-profile')` fallback (mutations are blocked in demo),
 * and a resolvable `company` keeps the follow-up
 * `findRecord('company', employee.company.id)` off `/companies/undefined`.
 */
export function buildDemoEmployeeDetailDoc(selection: ResolvedSelection): JsonApiDoc {
  const base = buildDemoEmployeeCatchAllDoc(selection);
  const data = base.data as JsonApiResource;
  // The SYF parent route reads `employee.matchParticipant` off THIS doc
  // (`/employees/<id>?include=match-participant.*`) — null bounces the whole
  // /syf tree back to the main dashboard. Sideload the enrolled participant
  // graph when the employerMatch product is selected (pure selection-derived,
  // same pattern as the PSLF NSLDS enrichment).
  const matchGraph = selection.productIds.includes('employerMatch')
    ? buildMatchParticipantGraph(resolveDemoFixtureIdentity(selection), {
        // The First Login persona is a brand-new employee, so SYF is served
        // NOT_STARTED and /syf lands on the shipped enrollment screens. Every
        // other persona keeps the enrolled mid-journey dashboard.
        enrolled: selection.userScenarioId !== 'first-login',
      })
    : null;
  data.relationships = {
    ...data.relationships,
    pslfProfile: { data: { type: 'pslf-profile', id: 'demo-pslf-profile-1' } },
    pslfForms: { data: [] },
    employeeAddition: { data: null },
    ...(matchGraph ? { matchParticipant: { data: matchGraph.participantRef } } : {}),
  };
  return {
    data,
    included: [
      ...(base.included ?? []),
      buildPslfProfileResource(),
      ...(matchGraph ? matchGraph.included : []),
    ],
  };
}

/**
 * Standalone `person` findRecord document — served by the catch-all
 * `findRecord('person')` op-matcher (registered LAST, so product person
 * fixtures — contributions/PSLF — still win via first-match-wins) and by the
 * `/people/<session-id>` fetch matcher. The handler echoes the requested id,
 * so any person lookup during a demo resolves to the session identity (same
 * names as the /me doc), carrying the debt-axis linked-loan graph (see
 * {@link buildPersonLoanGraph}), instead of falling through to the real API.
 */
export function buildDemoPersonDoc(selection: ResolvedSelection): JsonApiDoc {
  const { firstName, lastName } = resolveIdentityName(selection);
  const graph = buildPersonLoanGraph(selection);

  return {
    data: {
      type: 'person',
      id: PERSON_ID,
      attributes: { firstName, lastName, preferences: {} },
      relationships: { accounts: { data: graph.accountRefs } },
    },
    ...(graph.included.length ? { included: graph.included } : {}),
  };
}

/**
 * Display identities for the non-participant session roles. The user-scenario
 * personas (First Login, Power User, …) are PARTICIPANT-only employee journeys,
 * and the per-client `employee` slot is the client's flagship employee story —
 * deriving a supervisor/admin name from either renders the session user
 * literally named "Power User" in the header, `/me` identity, and admin
 * directory. These roles resolve to fixed believable names instead.
 */
const ROLE_IDENTITY_NAMES: Partial<Record<DemoSessionRoleId, IdentityName>> = {
  supervisor: { firstName: 'Morgan', lastName: 'Reyes' },
  admin: { firstName: 'Casey', lastName: 'Whitman' },
};

function resolveIdentityName(selection: ResolvedSelection): IdentityName {
  const roleIdentity = ROLE_IDENTITY_NAMES[selection.roleId];
  if (roleIdentity) {
    return roleIdentity;
  }

  const clientEmployee = getDemoData(selection.clientId).employee;
  if (clientEmployee?.firstName && clientEmployee?.lastName) {
    return { firstName: clientEmployee.firstName, lastName: clientEmployee.lastName };
  }

  const scenarioName = getDemoUserScenario(selection.userScenarioId).name?.trim();
  if (scenarioName) {
    const parts = scenarioName.split(/\s+/);
    const firstName = parts[0] ?? DEFAULT_FIRST_NAME;
    const lastName = parts.length > 1 ? parts.slice(1).join(' ') : DEFAULT_LAST_NAME;
    return { firstName, lastName };
  }

  return { firstName: DEFAULT_FIRST_NAME, lastName: DEFAULT_LAST_NAME };
}

/**
 * Shape a camelCase demo doc for the FETCH seam (the `window.fetch`
 * interceptor), so a builder read served there flows through the normal
 * response + cache pipeline exactly like a live response and `content.data`
 * materializes as the proper model.
 *
 * KEY FORMAT: this is a PASS-THROUGH — keys stay CAMELCASE. tio-employee
 * negotiates `x-json-api-key-format: camelized`, so the wire (and therefore
 * any fetch-seam fixture) is camelCase; the type-normalization handler only
 * camelizes keys for headerless apps. This seam used to dasherize keys (a
 * stale assumption from the headerless era), which made the schema layer
 * silently DROP every multi-word attribute (`isActive`, `firstName`,
 * `publicOrdinalValue`, …) on raw-served docs — e.g. the dashboard
 * loan-summary tile rendered $0.00 because the loan's `isActive` never
 * materialized. Kept as the seam marker + `included` normalizer. (The
 * request-HANDLER op-matchers consume the same camelCase docs directly via
 * `store.push` and do not use this.)
 */
export function toRawDoc(doc: JsonApiDoc): JsonApiDoc {
  return {
    data: doc.data,
    included: doc.included ?? [],
    ...(doc.meta ? { meta: doc.meta } : {}),
  };
}

/** The `/me` fetch-seam variant of {@link buildDemoIdentityDoc}. */
export function buildDemoIdentityRawDoc(selection: ResolvedSelection): JsonApiDoc {
  return toRawDoc(buildDemoIdentityDoc(selection));
}

/**
 * Build the fabricated `/me` JSON:API document for the active demo session.
 *
 * @param selection the active demo selection; `selection.roleId` decides the
 *   persisted `relationshipType` (PARTICIPANT / TAS.Approver.SUPERVISOR /
 *   EMPLOYEE_ADMIN) so the relationship and the localStorage role agree.
 */
export function buildDemoIdentityDoc(selection: ResolvedSelection): JsonApiDoc {
  const { firstName, lastName } = resolveIdentityName(selection);
  const employerName = getDemoData(selection.clientId).client.name;
  const relationshipType = getDemoSessionRole(selection.roleId).relationshipType;

  // Same linked-loan graph as the standalone person doc — the /me bootstrap
  // materializes the person first, so it must carry the debt-axis accounts or
  // the dashboard's include-read cache-hits an empty graph (see
  // buildPersonLoanGraph).
  const loanGraph = buildPersonLoanGraph(selection);
  const personResource: JsonApiResource = {
    type: 'person',
    id: PERSON_ID,
    attributes: {
      firstName,
      lastName,
      preferences: {},
    },
    relationships: {
      user: { data: { type: 'user', id: USER_ID } },
      employees: { data: [{ type: 'employee', id: EMPLOYEE_ID }] },
      accounts: { data: loanGraph.accountRefs },
    },
  };

  // Shared with the standalone company findRecord doc (buildDemoCompanyDoc) so
  // the sideloaded identity company and a fresh company lookup never diverge.
  const companySettingResource = buildDemoCompanySettingResource();
  const companyResource = buildDemoCompanyResource(employerName);

  // The demo identity employee carries the union of product relationships
  // (plan + tasParticipant) so `sessionContext.currentEmployee` is coherent for
  // EVERY product directly — without it, SLR/contributions consumers that read
  // currentEmployee.plan.cadence (no findRecord re-fetch, unlike TAS/PSLF) would
  // see an empty plan. Ids are reused from the product fixtures.
  // Same name derivation as the contributions product's plan fixture (which
  // shares this id), so the plan name cannot drift between the two pushes.
  const planResource: JsonApiResource = {
    type: 'plan',
    id: 'demo-slr-plan-1',
    attributes: {
      name: `${employerName} Student Loan Repayment`,
      companyKey: demoCompanyKey(employerName),
      cadence: 'MONTHLY',
      annualLimit: 150000,
      lifetimeLimit: 1200000,
      currency: 'USD',
      // Inline tier schedule (cents; $125/mo matches the contributions
      // product's payment fixtures). Live plans get tiers via the
      // /plans/:id/tiers request, which demo sessions never make —
      // scheduledMonthlyContribution reads this attribute instead.
      tiers: [{ fromCycle: 1, amount: 12500, per: 'MONTHLY' }],
    },
  };

  const tasParticipantResource: JsonApiResource = {
    type: 'tas-participant',
    id: 'demo-tas-participant-1',
    attributes: { state: 'TAS.ParticipantState.ACTIVE', employmentStartDate: '2022-01-15' },
    relationships: { tasEligibilities: { data: [] }, tasGroupings: { data: [] } },
  };

  // The SYF dashboard's beforeModel gate reads
  // `sessionContext.currentEmployee.matchParticipant.isEnrolled` off THIS
  // graph (not the /employees/<id> detail read), so the enrolled participant
  // must ride the /me bootstrap too — same employerMatch gating as
  // buildDemoEmployeeDetailDoc.
  const matchGraph = selection.productIds.includes('employerMatch')
    ? buildMatchParticipantGraph(resolveDemoFixtureIdentity(selection), {
        // The First Login persona is a brand-new employee, so SYF is served
        // NOT_STARTED and /syf lands on the shipped enrollment screens. Every
        // other persona keeps the enrolled mid-journey dashboard.
        enrolled: selection.userScenarioId !== 'first-login',
      })
    : null;
  const pslfProfile =
    selection.productIds.includes('pslf') && isPslfEligibleClient(selection.clientId)
      ? buildPslfProfileResource()
      : null;

  const employeeResource: JsonApiResource = {
    type: 'employee',
    id: EMPLOYEE_ID,
    attributes: { firstName, lastName, isActive: true },
    relationships: {
      company: { data: { type: 'company', id: COMPANY_ID } },
      person: { data: { type: 'person', id: PERSON_ID } },
      plan: { data: { type: 'plan', id: 'demo-slr-plan-1' } },
      tasParticipant: { data: { type: 'tas-participant', id: 'demo-tas-participant-1' } },
      ...(pslfProfile
        ? { pslfProfile: { data: { type: 'pslf-profile', id: pslfProfile.id } } }
        : {}),
      ...(matchGraph ? { matchParticipant: { data: matchGraph.participantRef } } : {}),
    },
  };

  // The jurisdiction is itself a user-role record (user-relationship.jurisdiction
  // is belongsTo('user-role')). Its `company` is what computedRoleViews reads;
  // its own `roleType` is never read by the getter (cosmetic).
  const jurisdictionRoleResource: JsonApiResource = {
    type: 'user-role',
    id: JURISDICTION_ROLE_ID,
    attributes: { roleType: relationshipType, isActive: true },
    relationships: {
      company: { data: { type: 'company', id: COMPANY_ID } },
    },
  };

  const userRelationshipResource: JsonApiResource = {
    type: 'user-relationship',
    id: USER_RELATIONSHIP_ID,
    attributes: { relationshipType, isActive: true },
    relationships: {
      role: { data: { type: 'user-role', id: USER_ROLE_ID } },
      jurisdiction: { data: { type: 'user-role', id: JURISDICTION_ROLE_ID } },
    },
  };

  const userRoleResource: JsonApiResource = {
    type: 'user-role',
    id: USER_ROLE_ID,
    attributes: { roleType: relationshipType, isActive: true },
    // No `user` back-link: user-role declares belongsTo('user', inverse: 'roles')
    // but the user model's hasMany is `userRoles` (inverse: 'user'), so a back-link
    // here breaks inverse resolution. The forward user.userRoles link is enough
    // for computedRoleViews.
    relationships: {
      relationships: {
        data: [{ type: 'user-relationship', id: USER_RELATIONSHIP_ID }],
      },
    },
  };

  const usageAgreementResource: JsonApiResource = {
    type: 'agreement',
    id: USAGE_AGREEMENT_ID,
    attributes: { classification: 'app_terms_of_use', isExpired: false },
  };

  const privacyAgreementResource: JsonApiResource = {
    type: 'agreement',
    id: PRIVACY_AGREEMENT_ID,
    attributes: { classification: 'app_privacy_policy', isExpired: false },
  };

  const customConditionResource: JsonApiResource = {
    type: 'condition',
    id: CUSTOM_CONDITION_ID,
    attributes: { classification: 'custom' },
  };

  const customAgreementResource: JsonApiResource = {
    type: 'agreement',
    id: CUSTOM_AGREEMENT_ID,
    attributes: { classification: 'custom', isExpired: false },
    relationships: {
      condition: { data: { type: 'condition', id: CUSTOM_CONDITION_ID } },
    },
  };

  const primaryEmailResource: JsonApiResource = {
    type: 'email-address',
    id: PRIMARY_EMAIL_ID,
    attributes: { email: PRIMARY_EMAIL_ADDRESS, contactType: 'PRIMARY', isActive: true },
  };

  const userResource: JsonApiResource = {
    type: 'user',
    id: USER_ID,
    attributes: {
      isActive: true,
      // Company-scoped role gates (partner reporting) check the claim's company ids.
      roleClaims: [
        {
          role: relationshipType,
          relationship_type: relationshipType,
          company_ids: [COMPANY_ID],
        },
      ],
    },
    relationships: {
      person: { data: { type: 'person', id: PERSON_ID } },
      employees: { data: [{ type: 'employee', id: EMPLOYEE_ID }] },
      userRoles: { data: [{ type: 'user-role', id: USER_ROLE_ID }] },
      usageAgreement: { data: { type: 'agreement', id: USAGE_AGREEMENT_ID } },
      privacyAgreement: { data: { type: 'agreement', id: PRIVACY_AGREEMENT_ID } },
      customRegisterAgreements: {
        data: [{ type: 'agreement', id: CUSTOM_AGREEMENT_ID }],
      },
      emailAddresses: { data: [{ type: 'email-address', id: PRIMARY_EMAIL_ID }] },
    },
  };

  return {
    data: userResource,
    included: [
      personResource,
      ...loanGraph.included,
      employeeResource,
      ...(pslfProfile ? [pslfProfile] : []),
      ...(matchGraph ? matchGraph.included : []),
      planResource,
      tasParticipantResource,
      companyResource,
      companySettingResource,
      userRoleResource,
      userRelationshipResource,
      jurisdictionRoleResource,
      usageAgreementResource,
      privacyAgreementResource,
      customAgreementResource,
      customConditionResource,
      primaryEmailResource,
    ],
  };
}
